import React from 'react';
import { connect } from 'react-redux';
import { Spinner, Alert, Container, Tab } from 'react-bootstrap';
import { SurveysAPI, AuthAPI } from '../../API';
import SurveyChangeLog from '../ChangeLog/SurveyChangeLog';
import { Form } from './Form';
import { DashboardDetails } from './DashboardDetails';
import { OverviewPane } from './redesign/OverviewPane';
import { MarketListCard } from './redesign/MarketsPane';
import { PanelLaunchPane } from './redesign/PanelLaunchPane';
import { FieldworkPane } from './redesign/FieldworkPane';
import { TestingPane } from './redesign/TestingPane';
import CardHeader from '../Card/CardHeader';
import { getSurveyDisplayName } from '../../Utils/surveyDisplayName';
import { isPanelCountryByName } from './surveyConstants';
import { DetailTabs, DetailTabKey, detailTabsFor, normalizeDetailTab } from './redesign/DetailTabs';
import { StatusConfirm } from './redesign/StatusConfirm';
import { StatusSelect } from './redesign/StatusSelect';
import { UnsavedConfirm } from './redesign/UnsavedConfirm';
import { isDirtyingEvent } from './redesign/dirtyTracking';
import { canSetStatus, marketsNotReadyForLive, projectWithoutMarketsNotReady, liveUrlMessage } from './redesign/statusGuard';
import Swal from 'sweetalert2';
import { statusLabel, statsBySurvey, InFieldRow, leadingTestRows, unwrapSurvey, marketLabels } from './redesign/surveyMetrics';
import './redesign/surveys-redesign.css';

// Form's exported type (withRouter(connect(reduxForm))) drops its own props;
// every other caller hits the same TS2322. Typed loosely here on purpose.
const SetupForm: React.ComponentType<any> = Form as any;

interface Props {
  role: string;
  match: { params: { id: string } };
  location: { pathname: string; search: string };
  history: { push: (path: string) => void; replace: (path: string) => void };
}

interface State {
  status: string; // 'None' | 'Loading' | 'Loaded' | 'Error'
  error: string | null;
  survey: any;
  children: any[];
  parent: any;
  pendingStatus: string | null;
  // The survey that pendingStatus is for: this page's survey (header) or one
  // market (an Overview market row).
  pendingTarget: any;
  // True while a confirmed status change is saving. The panes are disabled
  // until they remount with the new status, so a form save can't send the old one.
  statusSaving: boolean;
  // Bumped after a header status change so the header actions and the active
  // pane remount and re-read the survey. A Setup submit does not bump it, so
  // the inline Form keeps its post-submit feedback.
  paneKey: number;
  // Bumped after a Setup save so the header actions remount without touching
  // the Form (paneKey would unmount it and lose its inline upload feedback).
  actionsKey: number;
  // Set when a silent refresh of the already-loaded survey fails; the page stays.
  refreshError: string | null;
  // Approximate: set by change/input/option-click events inside a form pane.
  dirty: boolean;
  // Tab the user tried to open while dirty; non-null shows UnsavedConfirm.
  pendingTab: DetailTabKey | null;
  // Market the user tried to select while dirty; non-null shows UnsavedConfirm.
  pendingMarket: string | null;
  // The tab that market selection belongs to (Markets or Schedule).
  pendingMarketTab: DetailTabKey;
  // Tab badges, loaded with the page. A count whose fetch failed is absent.
  counts: Partial<Record<DetailTabKey, number | string>>;
  // In-field rows, fetched once and shared by Overview and the Markets list.
  inFieldRows: InFieldRow[];
  inFieldLoaded: boolean;
  // Markets created by a Setup/Markets save on this page; Markets shows them
  // a note until they go Live.
  newMarketIds: string[];
  // The last shared-form save's unique-link upload outcomes, for the form
  // remounted (at paneKey) after it; any other remount drops them.
  draftUploads: { paneKey: number; results: Record<string, any> } | null;
}

// Setup and Markets edit one shared draft (one Form): moving between them is
// not "leaving" and keeps unsaved changes.
const DRAFT_TABS: DetailTabKey[] = ['setup', 'markets'];
const isDraftTab = (k: DetailTabKey | null | undefined) => !!k && DRAFT_TABS.includes(k);
export const NEW_MARKET_NOTE = 'New market - add its survey links, then set it Live from Overview.';

// The requests behind the Testing and Activity badges are capped, so a full
// page is shown as "N+".
const TESTING_COUNT_LIMIT = 200;
const ACTIVITY_COUNT_LIMIT = 50;

class SurveyDetailPageComponent extends React.Component<Props, State> {
  private _isMounted = false;
  // The Testing tab's exact test-user count (it reads past the first page), kept
  // so a later badge refresh of the same survey does not replace it.
  private paneTestCount: { id: string; value: number | string } | null = null;

  constructor(props: Props) {
    super(props);
    this.state = {
      status: 'None',
      error: null,
      survey: null,
      children: [],
      parent: null,
      pendingStatus: null,
      pendingTarget: null,
      statusSaving: false,
      paneKey: 0,
      actionsKey: 0,
      refreshError: null,
      dirty: false,
      pendingTab: null,
      pendingMarket: null,
      pendingMarketTab: 'markets',
      counts: {},
      inFieldRows: [],
      inFieldLoaded: false,
      newMarketIds: [],
      draftUploads: null,
    };
  }

  componentDidMount() {
    this._isMounted = true;
    this.fetchData();
  }

  componentDidUpdate(prevProps: Props) {
    // Breadcrumb parent <-> child navigation changes only the id param.
    if (prevProps.match.params.id !== this.props.match.params.id) {
      this.setState((p) => ({ dirty: false, pendingTab: null, pendingMarket: null, paneKey: p.paneKey + 1, counts: {}, inFieldRows: [], inFieldLoaded: false, newMarketIds: [] }));
      this.fetchData();
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  fetchData = async () => {
    const { id } = this.props.match.params;
    // Spinner only on first load or a different survey. A refresh of the same
    // survey (after a status change, Setup submit or header action) keeps the
    // page mounted, so an inline Form is not torn down mid-submit.
    const sameSurvey = this.state.survey && String(this.state.survey.id) === String(id);
    try {
      if (!sameSurvey) this.setState({ status: 'Loading', error: null });

      const response: any = await SurveysAPI.getOneDetails(id, TESTING_COUNT_LIMIT, 1);
      const surveyData = unwrapSurvey(response);

      let children: any[] = [];
      let parent: any = null;
      // If parentid is falsy, it's a parent survey
      if (!surveyData.parentid) {
        const childRes: any = await SurveysAPI.getChildSurveys(id);
        children = childRes.data || childRes || [];
      } else {
        // Child: the parent row feeds the breadcrumb and the meta line only,
        // so a failure here must not take the page down.
        try {
          const parentRes: any = await SurveysAPI.getOneDetails(surveyData.parentid, 1, 1);
          parent = unwrapSurvey(parentRes);
        } catch (e) {
          parent = null;
        }
      }

      if (!this._isMounted || this.props.match.params.id !== id) return;
      this.setState({
        status: 'Loaded',
        survey: surveyData,
        children,
        parent,
        refreshError: null,
      });
      this.loadCounts(id, surveyData, children, response);
      this.loadMarketStats(id);
    } catch (error: any) {
      if (!this._isMounted || this.props.match.params.id !== id) return;
      const message = error.message || 'Failed to fetch survey details';
      if (sameSurvey) {
        // Silent refresh failed: keep the loaded page and warn instead.
        this.setState({ refreshError: message });
        return;
      }
      this.setState({ status: 'Error', error: message });
    }
  };

  // Tab badges. Each source fails on its own and then simply has no badge.
  // Testing reuses the survey response (users array); the others are one request each.
  loadCounts = async (id: string, surveyData: any, children: any[], response: any) => {
    const isChild = !!surveyData.parentid;
    // Test rows lead the user list (see leadingTestRows); a page that is all
    // Test rows shows "N+" until the Testing tab has counted the rest.
    const users = response?.data?.users;
    const firstPage = Array.isArray(users) ? leadingTestRows(users, TESTING_COUNT_LIMIT) : null;
    const fromPane = this.paneTestCount && this.paneTestCount.id === String(id) ? this.paneTestCount.value : undefined;
    const testing = fromPane !== undefined
      ? fromPane
      : (firstPage ? (firstPage.complete ? firstPage.count : `${firstPage.count}+`) : undefined);
    const [partners, activity] = await Promise.all([
      Promise.resolve()
        .then(() => SurveysAPI.getpantnerdetailsmain(surveyData.id))
        .then((rows: any) => (Array.isArray(rows) ? rows.length : undefined))
        .catch(() => undefined),
      Promise.resolve()
        .then(() => AuthAPI.changeLogsForSurvey(id, ACTIVITY_COUNT_LIMIT))
        .then((rows: any) => (Array.isArray(rows)
          ? (rows.length >= ACTIVITY_COUNT_LIMIT ? `${ACTIVITY_COUNT_LIMIT}+` : rows.length)
          : undefined))
        .catch(() => undefined),
    ]);
    if (!this._isMounted || this.props.match.params.id !== id) return;
    const counts: State['counts'] = { markets: isChild ? 1 : children.length };
    if (partners !== undefined) counts.partners = partners;
    if (testing !== undefined) counts.testing = testing;
    if (activity !== undefined) counts.activity = activity;
    this.setState({ counts });
  };

  // Per child totals for the Markets list. Failure just leaves the list without them.
  loadMarketStats = async (id: string) => {
    try {
      const res: any = await SurveysAPI.getInFieldStats(id);
      if (!this._isMounted || this.props.match.params.id !== id) return;
      this.setState({ inFieldRows: (res?.stats || []) as InFieldRow[], inFieldLoaded: true });
    } catch (e) {
      // No stats: tiles and lists fall back to zeros.
      if (this._isMounted && this.props.match.params.id === id) this.setState({ inFieldRows: [], inFieldLoaded: true });
    }
  };

  // The Testing tab reports its total so the tab badge matches "All markets".
  onTestCount = (value: number | string) => {
    this.paneTestCount = { id: String(this.state.survey?.id ?? ''), value };
    this.setState((p) => ({ counts: { ...p.counts, testing: value } }));
  };

  onSetupSaved = () => {
    this.fetchData();
    this.setState((p) => ({ actionsKey: p.actionsKey + 1, dirty: false }));
  };

  // A save from the shared Setup/Markets form. The form is then rebuilt from
  // the saved survey (remounted, so it reloads from the server): each market
  // it sent as new is now the saved child, with its id and saved links, and no
  // unsaved entry stays behind. The save's upload outcomes go back to it.
  onDraftSaved = async (uploadResults?: Record<string, any>) => {
    const before = new Set(this.marketsList().map((m: any) => String(m.id)));
    this.setState((p) => ({ actionsKey: p.actionsKey + 1, dirty: false }));
    await this.fetchData();
    if (!this._isMounted) return;
    const added = this.marketsList().filter((m: any) => !before.has(String(m.id))).map((m: any) => String(m.id));
    this.setState((p) => ({
      paneKey: p.paneKey + 1,
      draftUploads: { paneKey: p.paneKey + 1, results: uploadResults || {} },
      newMarketIds: [...p.newMarketIds, ...added],
    }));
    // Markets had an unsaved market selected (`new:<code>:<nonce>`): select
    // the market that save created for it.
    const params = new URLSearchParams(this.props.location.search);
    const market = params.get('market') || '';
    if (normalizeDetailTab(params.get('tab')) === 'markets' && market.startsWith('new:')) {
      const code = market.split(':')[1];
      const hit = this.marketsList().find((m: any) => added.includes(String(m.id)) && m.shortName === code);
      if (hit) this.navigateToMarket(String(hit.id));
    }
  };

  // Discard reloads the survey by remounting the pane; Form.reset() would wipe
  // the form to empty create-mode defaults.
  onDiscard = () => {
    this.setState((p) => ({ paneKey: p.paneKey + 1, dirty: false }));
  };

  markDirty = (e: React.SyntheticEvent) => {
    if (!this.state.dirty && isDirtyingEvent(e.target, e.type)) this.setState({ dirty: true });
  };

  navigateToTab = (k: DetailTabKey) => {
    const { location, history } = this.props;
    history.replace(`${location.pathname}?tab=${k}`);
  };

  selectTab = (k: DetailTabKey) => {
    const current = normalizeDetailTab(new URLSearchParams(this.props.location.search).get('tab')) || 'overview';
    if (k === current) return;
    // Setup <-> Markets keeps the shared draft, so it never asks.
    if (this.state.dirty && !(isDraftTab(current) && isDraftTab(k))) {
      this.setState({ pendingTab: k });
      return;
    }
    this.navigateToTab(k);
  };

  leaveWithoutSaving = () => {
    const { pendingTab, pendingMarket, pendingMarketTab } = this.state;
    this.setState({ dirty: false, pendingTab: null, pendingMarket: null });
    if (pendingTab) this.navigateToTab(pendingTab);
    else if (pendingMarket) this.navigateToMarket(pendingMarket, pendingMarketTab);
  };

  // Overview links: Markets opens on a market via ?market=<childId>.
  goTo = (tab: DetailTabKey, market?: string) => {
    const { location, history } = this.props;
    const q = market ? `&market=${encodeURIComponent(market)}` : '';
    history.replace(`${location.pathname}?tab=${tab}${q}`);
  };

  // A child page lists only itself; a parent lists its children.
  marketsList = (): any[] => {
    const { survey, children } = this.state;
    return survey?.parentid ? [survey] : children;
  };

  // Samples and Schedule list panel markets only, so their default and their
  // ?market= match are limited to them (Markets uses the full list, see
  // renderDraftLayout).
  panelMarketsList = (): any[] => this.marketsList().filter((m: any) => isPanelCountryByName(m.country));

  selectedPanelMarketId = (): string => {
    const markets = this.panelMarketsList();
    const market = new URLSearchParams(this.props.location.search).get('market');
    const hit = market ? markets.find((m: any) => String(m.id) === market) : undefined;
    return String((hit || markets[0])?.id ?? '');
  };

  navigateToMarket = (marketId: string, tab: DetailTabKey = 'markets') => {
    const { location, history } = this.props;
    history.replace(`${location.pathname}?tab=${tab}&market=${encodeURIComponent(marketId)}`);
  };

  // Opens a just-created same-market sibling. A parent page re-fetches so the
  // Markets list includes it, then remounts the shared form (so it loads the
  // sibling; unsaved changes go, as the confirm said) and selects it; a child
  // page is a different survey, so it navigates to the sibling's own page.
  openNewSibling = async (newId: string) => {
    const { survey } = this.state;
    const { role, history } = this.props;
    if (survey?.parentid) {
      history.push(`/${role}/surveys/details/${newId}?tab=markets`);
    } else {
      await this.fetchData();
      if (!this._isMounted) return;
      this.setState((p) => ({ paneKey: p.paneKey + 1 }));
      this.navigateToMarket(newId);
    }
  };

  // Markets pane "+ Add another <market>": duplicates that child into a new sibling
  // (the same call as the header Duplicate), then opens it.
  onAddAnother = async (market: any) => {
    const label = market.country || market.shortName || 'market';
    const result = await Swal.fire({
      title: 'Add another market?',
      text: `This creates a new ${label} segment under this project. It starts in Test status.`
        + (this.state.dirty ? ' Unsaved changes in this market will be lost.' : ''),
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Add',
      cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;
    let respData: any;
    try {
      // Same response read as DashboardDetails.handleDuplicateChild.
      // A new market: Point Allocation Auto and Email only, not the source's values.
      respData = await SurveysAPI.duplicateChild(market.id, { newMarketDefaults: true });
    } catch (err: any) {
      const msg = (err && err.response && err.response.data && err.response.data.message) || (err && err.message) || 'Failed to duplicate.';
      Swal.fire({ icon: 'error', title: 'Error', text: msg });
      return;
    }
    // The duplicate exists now: a failure below must not show the error Swal.
    const newId = respData && respData.Survey && respData.Survey.id;
    const needsLinks = !!(respData && (respData.needsUniqueLinksUpload || respData.Survey?.needsUniqueLinksUpload));
    Swal.fire({
      icon: 'success',
      title: 'Done',
      text: 'Duplicate created successfully.'
        + (needsLinks ? ' Upload unique links for the new segment in its Markets tab before testing.' : ''),
    });
    // The user confirmed (with the unsaved-changes warning when dirty).
    this.setState({ dirty: false });
    try {
      if (newId) await this.openNewSibling(String(newId));
      else await this.fetchData();
    } catch (e) {
      console.error('Opening the new market failed:', e);
    }
  };

  // Switching market stays on whichever of the two tabs is open. Schedule holds
  // the inline panel launch settings form, so a dirty one asks first (Samples
  // has modal forms only and is never dirty).
  selectPanelMarket = (marketId: string) => {
    if (marketId === this.selectedPanelMarketId()) return;
    const tab = normalizeDetailTab(new URLSearchParams(this.props.location.search).get('tab'));
    const target: DetailTabKey = tab === 'schedule' ? 'schedule' : 'samples';
    if (this.state.dirty) {
      this.setState({ pendingMarket: marketId, pendingMarketTab: target });
      return;
    }
    this.navigateToMarket(marketId, target);
  };

  // Markets: every market is a pane of the one shared form, so switching keeps
  // unsaved changes and never asks.
  selectMarket = (marketId: string) => {
    this.navigateToMarket(marketId);
  };

  saveBarStatus = () => (this.state.dirty
    ? <span className="text-muted small"><span className="dx-savebar-dot" />Unsaved changes</span>
    : <span className="text-muted small">All changes saved</span>);

  // Form panes track edits (approximately) through bubbled change/input/click
  // events. Bubble phase on purpose: React 17 runs capture handlers in their own
  // dispatch, so a setState there re-renders the controlled field back to its
  // old value before the field's onChange runs, and the first edit is lost.
  dirtyWrap = (node: React.ReactNode, className = 'dx-card') => (
    <div
      className={className || undefined}
      onChange={this.markDirty}
      onInput={this.markDirty}
      onClick={this.markDirty}
    >
      {node}
    </div>
  );

  // Header (target = this page's survey; a parent also changes every market)
  // or an Overview market row (target = that child). Same guards for both,
  // then the confirmation.
  requestStatus = (target: any, next: string) => {
    const { survey, parent, children } = this.state;
    if (!target || !canSetStatus(target.expiryDate, next)) return;
    if (next === 'Open') {
      const isPage = String(target.id) === String(survey?.id);
      const notReady = isPage
        ? [
          ...projectWithoutMarketsNotReady(survey, children),
          ...marketsNotReadyForLive(survey, parent, children),
        ]
        // A market row on a parent page: the page survey is its parent.
        : marketsNotReadyForLive(target, survey, []);
      if (notReady.length > 0) {
        Swal.fire({ icon: 'warning', title: 'Live URL missing', text: liveUrlMessage(notReady) });
        return;
      }
    }
    this.setState({ pendingStatus: next, pendingTarget: target });
  };

  confirmStatus = async () => {
    const { survey, pendingStatus, pendingTarget } = this.state;
    const target = pendingTarget || survey;
    if (!target || !pendingStatus) return;
    this.setState({ pendingStatus: null, pendingTarget: null, statusSaving: true });
    try {
      await SurveysAPI.updateSurveyStatusOnly({ surveyId: target.id, status: pendingStatus });
    } catch (error) {
      console.error('Status change error:', error);
      const serverMessage = (error as any)?.response?.data?.message || (error as any)?.message;
      if (serverMessage) {
        Swal.fire({ icon: 'warning', title: 'Status not changed', text: String(serverMessage) });
      }
    }
    await this.fetchData();
    if (this._isMounted) this.setState((prev) => ({ paneKey: prev.paneKey + 1, dirty: false, statusSaving: false }));
  };

  renderMeta = () => {
    const { survey, children, parent } = this.state;
    const parts: string[] = [];
    if (survey.parentid) {
      const country = survey.country
        ? (survey.shortName ? `${survey.country} (${survey.shortName})` : survey.country)
        : '';
      if (country) parts.push(country);
      parts.push(isPanelCountryByName(survey.country) ? 'DataXing panel market' : 'external market');
      const parentName = getSurveyDisplayName(parent);
      if (parentName) parts.push(`child of ${parentName}`);
    } else {
      if (survey.projectId) parts.push(String(survey.projectId));
      if (survey.uniqueid) parts.push(`#${survey.uniqueid}`);
      if (survey.client) parts.push(String(survey.client));
      if (survey.pmName) parts.push(`PM ${survey.pmName}`);
      parts.push(`${children.length} markets`);
    }
    return <div className="dx-meta">{parts.join(' · ')}</div>;
  };

  // The Markets list: the saved markets (server rows), then the pending ones:
  // form tabs that have no child id because they are not saved yet (key
  // `new:<code>:<nonce>`). A tab that has an id is a saved market, listed from
  // the server rows only, so one market is never listed twice.
  draftMarkets = (parts: any): any[] => {
    const saved = this.marketsList();
    const childStates = (parts.state && parts.state.childStates) || {};
    const drafts = (parts.markets || [])
      .filter((t: any) => !(t.option && t.option.id) && !(childStates[t.childKey] || {}).id)
      .map((t: any) => {
        const c = childStates[t.childKey] || {};
        return { id: t.childKey, shortName: t.shortName, country: t.countryName, surveyType: c.surveyType, userLimitCommitted: c.userLimitCommitted, draft: true };
      });
    return [...saved, ...drafts];
  };

  // Setup and Markets around the shared form's panes (Form.renderLayout). The
  // panes and save bar keep one position on both tabs (only wrapper classes
  // change), so the panes never remount and the draft survives the switch.
  renderDraftLayout = (tab: DetailTabKey, parts: any, readOnly: (node: React.ReactNode) => React.ReactNode) => {
    const { survey, newMarketIds } = this.state;
    const isSales = this.props.role === 'sales';
    const onMarkets = tab === 'markets';
    const markets = this.draftMarkets(parts);
    const param = new URLSearchParams(this.props.location.search).get('market');
    const selected = markets.find((m: any) => String(m.id) === param) || markets[0];
    const inForm = !!selected && (parts.markets || []).some((t: any) => String(t.childKey) === String(selected.id));
    const activeKey = onMarkets ? (inForm ? `child-${selected.id}` : 'none') : 'parent';
    const showNote = onMarkets && !!selected && newMarketIds.includes(String(selected.id)) && selected.surveyType !== 'Open';
    return (
      <div className={onMarkets ? 'row' : undefined}>
        {onMarkets ? (
          <div className="col-md-3">
            <MarketListCard
              markets={markets}
              selectedId={selected ? String(selected.id) : ''}
              onSelect={this.selectMarket}
              stats={statsBySurvey(this.state.inFieldRows)}
              isSales={isSales}
              canAddMarket={!survey.parentid}
              onAddMarket={() => this.selectTab('setup')}
              onAddAnother={this.onAddAnother}
            />
          </div>
        ) : null}
        <div className={onMarkets ? 'col-md-9' : undefined}>
          {onMarkets && !selected ? <div className="dx-card text-muted">No market selected.</div> : null}
          {showNote ? <div className="alert alert-info py-2 small dx-new-market-note">{NEW_MARKET_NOTE}</div> : null}
          <div className={`dx-card${onMarkets && !selected ? ' d-none' : ''}`}>
            {/* A saved market taken out of Setup's Countries in this draft (none
                while the form is still loading its markets). */}
            {onMarkets && selected && !inForm && (parts.markets || []).length > 0
              ? <div className="text-muted">This market is not in the current Setup countries.</div>
              : null}
            {readOnly(
              <>
                <Tab.Container activeKey={activeKey}>{parts.panes}</Tab.Container>
                <div className="dx-savebar d-flex align-items-center mt-2">
                  {this.saveBarStatus()}
                  <div className="ml-auto d-flex align-items-center">
                    <button type="button" onClick={this.onDiscard} className="btn btn-light mr-2">Discard</button>
                    <button type="submit" disabled={!parts.canSubmit} className="btn btn-primary mr-3">Save changes</button>
                    {parts.submitting && <Spinner animation="border" variant="primary" />}
                  </div>
                </div>
              </>,
            )}
          </div>
        </div>
      </div>
    );
  };

  renderPane = (tab: DetailTabKey) => {
    const { survey, draftUploads } = this.state;
    const { role } = this.props;
    const { id } = this.props.match.params;
    const isSales = role === 'sales';
    const readOnly = (node: React.ReactNode) => (isSales ? <fieldset disabled>{node}</fieldset> : node);

    switch (tab) {
      case 'overview':
        return (
          <OverviewPane
            survey={survey}
            markets={this.marketsList()}
            inFieldRows={this.state.inFieldRows}
            inFieldLoaded={this.state.inFieldLoaded}
            role={role}
            onStatusSelect={this.requestStatus}
            onNavigate={(t) => this.goTo(t)}
          />
        );
      case 'setup':
      case 'markets':
        // One Form for both tabs (render() keeps it mounted between them), so a
        // market added on Setup is on Markets right away and one Save saves both.
        return this.dirtyWrap(
          <SetupForm
            id={id}
            inline
            show
            hideTabNav
            // Status changes only from the header and Overview dropdowns.
            hideSurveyStatus
            role={role}
            childFieldGroup="market"
            onClose={() => this.selectTab('overview')}
            onRefresh={this.onDraftSaved}
            initialUploadResults={draftUploads && draftUploads.paneKey === this.state.paneKey ? draftUploads.results : undefined}
            onDiscard={this.onDiscard}
            // A new function on every render on purpose: Form skips re-rendering
            // on unchanged props, and the layout follows the tab and market.
            renderLayout={(parts: any) => this.renderDraftLayout(tab, parts, readOnly)}
          />,
          '',
        );
      case 'partners':
        return this.dirtyWrap(readOnly(
          <SetupForm
            key={`partners-${this.state.paneKey}`}
            id={survey.id}
            inline
            show
            hideTabNav
            hideSurveyStatus
            role={role}
            initialTab="partners"
            onRefresh={this.onSetupSaved}
            onDiscard={this.onDiscard}
            saveBarStatus={this.saveBarStatus()}
          />
        ));
      case 'testing':
        return (
          <TestingPane
            survey={survey}
            markets={this.marketsList()}
            canTest={!isSales}
            onAllCount={this.onTestCount}
          />
        );
      case 'fieldwork':
        return (
          <FieldworkPane
            survey={survey}
            markets={this.marketsList()}
            inFieldRows={this.state.inFieldRows}
            inFieldLoaded={this.state.inFieldLoaded}
            isSales={isSales}
          />
        );
      case 'activity':
        return (
          <div className="dx-card">
            <SurveyChangeLog surveyId={id} limit={ACTIVITY_COUNT_LIMIT} />
          </div>
        );
      case 'samples':
      case 'schedule':
        return (
          <PanelLaunchPane
            survey={survey}
            markets={this.panelMarketsList()}
            selectedId={this.selectedPanelMarketId()}
            onSelect={this.selectPanelMarket}
            section={tab}
            // Schedule: the market's panel launch settings, its own Form like
            // the Markets tab (same save bar, submit and unsaved-changes guard).
            renderLaunchSettings={(market) => this.dirtyWrap(readOnly(
              <>
                <h6>Panel launch settings</h6>
                <SetupForm
                  key={`launch-${market.id}-${this.state.paneKey}`}
                  id={survey.id}
                  inline
                  show
                  hideTabNav
                  hideSurveyStatus
                  role={role}
                  initialTab={`child-${market.id}`}
                  childFieldGroup="launch"
                  onRefresh={this.onSetupSaved}
                  onDiscard={this.onDiscard}
                  saveBarStatus={this.saveBarStatus()}
                />
              </>
            ))}
          />
        );
      default:
        return null;
    }
  };

  render() {
    const { status, error, survey, children, parent, pendingStatus, paneKey, actionsKey, refreshError, dirty, pendingTab, pendingMarket, counts } = this.state;
    const { role, location } = this.props;
    const { id } = this.props.match.params;
    const isSales = role === 'sales';

    if (status === 'Loading' || status === 'None') {
      return (
        <Container className="d-flex justify-content-center align-items-center mt-5">
          <Spinner animation="border" role="status">
            <span className="sr-only">Loading...</span>
          </Spinner>
        </Container>
      );
    }

    if (status === 'Error') {
      return (
        <div className="dx-surveys">
          <nav className="dx-breadcrumb"><a href={`#/${role}/surveys`}>Surveys</a></nav>
          <Alert variant="danger">{error}</Alert>
        </div>
      );
    }

    const isChild = !!survey.parentid;
    const hasPanelMarket = isChild
      ? isPanelCountryByName(survey.country)
      : children.some((c: any) => isPanelCountryByName(c.country));
    const tabs = detailTabsFor({ isChild, hasPanelMarket, isSales });
    // Old ?tab=panelLaunch links resolve to Samples before the tab fallback.
    const requested = normalizeDetailTab(new URLSearchParams(location.search).get('tab'));
    const activeTab: DetailTabKey = requested && tabs.includes(requested) ? requested : 'overview';
    const name = getSurveyDisplayName(survey);
    // A parent whose markets don't all share one status.
    const liveChildren = isChild ? [] : children.filter((c: any) => c && !c.deletedAt);
    const mixed = !isChild && new Set(liveChildren.map((c: any) => c.surveyType)).size > 1;
    const confirmTarget = this.state.pendingTarget || survey;
    const labels = marketLabels(liveChildren);
    const confirmMarkets = liveChildren.map((c: any) => `${labels[c.id]} · ${c.country} (now ${statusLabel(c.surveyType)})`);

    return (
      <div className="dx-surveys">
        <nav className="dx-breadcrumb">
          <a href={`#/${role}/surveys`}>Surveys</a>
          {isChild && (
            <>
              {' › '}
              <a href={`#/${role}/surveys/details/${survey.parentid}`}>{getSurveyDisplayName(parent) || 'Parent survey'}</a>
            </>
          )}
          {' › '}
          <span>{name}</span>
        </nav>

        <div className="w-100 mb-3">
          <CardHeader color="primary">
            <div className="d-flex flex-wrap justify-content-between align-items-center">
              <div className="mr-3">
                <h4 className="mb-0">{name}</h4>
                {this.renderMeta()}
              </div>
              <div className="d-flex flex-wrap align-items-center dx-head-actions">
                <label htmlFor={isSales ? undefined : 'detailStatus'} className="mb-0 mr-2 dx-status-label">Status</label>
                <div className="d-flex flex-column mr-3">
                  <StatusSelect
                    id="detailStatus"
                    role={role}
                    value={survey.surveyType}
                    onSelect={(next) => this.requestStatus(survey, next)}
                  />
                  {mixed && <small className="dx-status-mixed">Mixed: markets differ</small>}
                </div>
                {!isSales && (
                  <DashboardDetails
                    key={`actions-${paneKey}-${actionsKey}`}
                    id={id}
                    unique_id={survey.uniqueid}
                    inline
                    sections={['actions']}
                    onClose={() => {}}
                    onUpdate={(param: string, newId: any) => {
                      if (param === 'clone' && newId) this.props.history.push(`/${role}/surveys/details/${newId}`);
                      else if (param === 'duplicate' && newId) {
                        this.setState({ dirty: false });
                        this.props.history.push(`/${role}/surveys/details/${newId}?tab=markets`);
                      } else this.fetchData();
                    }}
                    onDelete={() => this.props.history.push(`/${role}/surveys`)}
                  />
                )}
              </div>
            </div>
          </CardHeader>
        </div>

        {refreshError && (
          <Alert variant="warning" dismissible onClose={() => this.setState({ refreshError: null })}>
            Couldn't refresh the survey details: {refreshError}
          </Alert>
        )}

        <DetailTabs tabs={tabs} active={activeTab} counts={counts} onSelect={this.selectTab} />

        <fieldset disabled={this.state.statusSaving}>
          {/* Setup and Markets share one key: the shared form stays mounted between them. */}
          <div key={`${isDraftTab(activeTab) ? 'draft' : activeTab}-${paneKey}`}>
            {this.renderPane(activeTab)}
          </div>
        </fieldset>

        {!isSales && (
          <StatusConfirm
            show={!!pendingStatus}
            name={confirmTarget === survey ? name : getSurveyDisplayName(confirmTarget)}
            from={confirmTarget.surveyType}
            to={pendingStatus || undefined}
            childCount={0}
            markets={confirmTarget === survey && !isChild ? confirmMarkets : undefined}
            setupOpen={activeTab === 'setup' || activeTab === 'markets' || activeTab === 'partners'}
            onCancel={() => this.setState({ pendingStatus: null, pendingTarget: null })}
            onConfirm={this.confirmStatus}
          />
        )}

        <UnsavedConfirm
          show={!!pendingTab || !!pendingMarket}
          onStay={() => this.setState({ pendingTab: null, pendingMarket: null })}
          onLeave={this.leaveWithoutSaving}
        />
      </div>
    );
  }
}

const mapStateToProps = (state: any) => ({
  role: state.adminUser?.adminUser?.role || 'admin'
});

export const SurveyDetailPage = connect(mapStateToProps)(SurveyDetailPageComponent);
export default SurveyDetailPage;
